import { HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, of, Subscription, throwError, timer } from 'rxjs';
import { catchError, finalize, mapTo, shareReplay, tap } from 'rxjs/operators';
import { HttpConfigService } from '../httpConfig/httpConfigService';
import { BYPASS_AUTH_HEADER, BYPASS_SERVER_ERROR_INTERCEPTOR } from '../httpConfig/http-context-tokens';

export interface RefreshTokenResponse {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiresInMs?: number;
  refreshTokenExpiresAt?: number;
  sessionId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private myToken: string;
  private refreshRequest$: Observable<RefreshTokenResponse>;
  private sessionMaintenance$: Subscription;
  private sessionMaintenanceStarted = false;
  private readonly accessTokenRefreshBufferMs = 60_000;
  private readonly refreshTokenRefreshBufferMs = 5 * 60_000;
  private readonly fallbackSessionMaintenanceMs = 15 * 60_000;
  private readonly failedRefreshRetryMs = 60_000;
  private readonly minimumMaintenanceDelayMs = 5_000;

  constructor(private injector: Injector) {}

  private readonly handleVisibilityChange = () => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      this.refreshSessionIfNeeded();
    }
  };

  private readonly handleConnectivityRestored = () => this.refreshSessionIfNeeded();

  /**
   * create token header
   * @returns
   */
  public createTokenHeader(headers: HttpHeaders = new HttpHeaders()): HttpHeaders {
    let reqOptions = headers.set('Content-Type', 'application/json');
    this.token = localStorage.getItem('token');
    if (this.token) {
      reqOptions = reqOptions
        .set('Authorization', `Bearer ${this.token}`);
    }
    return reqOptions;
  }

  get token(): string {
    return this.myToken;
  }

  set token(token: string) {
    this.myToken = token;
  }

  public setToken(authResponse: string | RefreshTokenResponse) {
    if (!authResponse) {
      return;
    }

    if (typeof authResponse === 'string') {
      this.setAccessToken(authResponse);
      this.setAccessTokenExpiry(authResponse, null);
      return;
    }

    const token = authResponse.token || authResponse.accessToken;
    if (token) {
      this.setAccessToken(token);
      this.setAccessTokenExpiry(token, authResponse.accessTokenExpiresInMs);
    }

    if (authResponse.refreshToken) {
      localStorage.setItem('refreshToken', authResponse.refreshToken);
    }

    if (authResponse.refreshTokenExpiresAt) {
      const refreshTokenExpiresAt = this.normalizeEpochMs(authResponse.refreshTokenExpiresAt);
      if (refreshTokenExpiresAt) {
        localStorage.setItem('refreshTokenExpiresAt', String(refreshTokenExpiresAt));
      }
    }

    if (authResponse.sessionId) {
      localStorage.setItem('authSessionId', String(authResponse.sessionId));
    }

    if (this.hasRefreshToken()) {
      this.startSessionMaintenance();
    }
  }

  public hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  public hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  public getAccessToken(): string {
    return localStorage.getItem('token');
  }

  public getRefreshToken(): string {
    return localStorage.getItem('refreshToken');
  }

  public getSessionId(): number {
    const sessionId = Number(localStorage.getItem('authSessionId'));
    return sessionId || null;
  }

  public canRestoreSession(): boolean {
    return this.hasAccessToken() || this.hasRefreshToken();
  }

  public hasValidAccessToken(): boolean {
    return this.hasAccessToken() && !this.shouldRefreshAccessToken();
  }

  public shouldRefreshAccessToken(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }

    const expiresAt = this.getAccessTokenExpiresAt();
    if (!expiresAt) {
      return false;
    }

    return Date.now() + this.accessTokenRefreshBufferMs >= expiresAt;
  }

  public startSessionMaintenance(): void {
    if (!this.hasRefreshToken()) {
      return;
    }

    if (!this.sessionMaintenanceStarted) {
      this.sessionMaintenanceStarted = true;

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
      }

      if (typeof window !== 'undefined') {
        window.addEventListener('online', this.handleConnectivityRestored);
      }
    }

    this.scheduleSessionMaintenance();
  }

  public stopSessionMaintenance(): void {
    this.cancelSessionMaintenance();

    if (!this.sessionMaintenanceStarted) {
      return;
    }

    this.sessionMaintenanceStarted = false;

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleConnectivityRestored);
    }
  }

  public restoreSession(): Observable<boolean> {
    if (this.hasValidAccessToken()) {
      this.startSessionMaintenance();
      return of(true);
    }

    if (!this.hasRefreshToken()) {
      this.clearToken();
      return of(false);
    }

    return this.refreshSession().pipe(
      mapTo(true),
      catchError((error) => {
        if (this.shouldEndSessionForRefreshError(error)) {
          this.clearToken();
          return of(false);
        }

        return of(this.hasAccessToken());
      })
    );
  }

  public refreshSession(): Observable<RefreshTokenResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError('Missing refresh token');
    }

    if (this.isRefreshTokenExpired()) {
      return throwError({ status: 401, error: 'Refresh token expired' });
    }

    if (!this.refreshRequest$) {
      const apiConfig = this.injector.get(HttpConfigService);
      const context = new HttpContext()
        .set(BYPASS_SERVER_ERROR_INTERCEPTOR, true)
        .set(BYPASS_AUTH_HEADER, true);
      const sessionId = this.getSessionId();
      const payload = {
        refreshToken,
        device: this.getDeviceContext(),
        ...(sessionId ? { sessionId } : {})
      };
      this.refreshRequest$ = apiConfig.post(
        `${apiConfig.getBaseUrl}auth/refresh`,
        payload,
        { context, withCredentials: true }
      ).pipe(
        tap((response: RefreshTokenResponse) => this.setToken(response)),
        finalize(() => this.refreshRequest$ = null),
        shareReplay(1)
      );
    }

    return this.refreshRequest$;
  }

  public shouldEndSessionForRefreshError(error: any): boolean {
    const status = Number(error?.status);
    return status === 400 || status === 401 || status === 403;
  }

  public getDeviceContext() {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    return {
      deviceId: this.getDeviceId(),
      platform: this.getPlatform(userAgent),
      deviceName: this.getDeviceName(userAgent),
      userAgent
    };
  }

  private setAccessToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  private setAccessTokenExpiry(token: string, expiresInMs: number) {
    const expiresAt = expiresInMs
      ? Date.now() + expiresInMs
      : this.getJwtExpiresAt(token);

    if (expiresAt) {
      localStorage.setItem('accessTokenExpiresAt', String(expiresAt));
    }
  }

  private getJwtExpiresAt(token: string): number {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        normalizedPayload.length + (4 - normalizedPayload.length % 4) % 4,
        '='
      );
      const parsed = JSON.parse(atob(paddedPayload));
      return parsed.exp ? parsed.exp * 1000 : null;
    } catch (err) {
      return null;
    }
  }

  private getAccessTokenExpiresAt(): number {
    return this.normalizeEpochMs(localStorage.getItem('accessTokenExpiresAt'));
  }

  private getRefreshTokenExpiresAt(): number {
    return this.normalizeEpochMs(localStorage.getItem('refreshTokenExpiresAt'));
  }

  private isRefreshTokenExpired(): boolean {
    const expiresAt = this.getRefreshTokenExpiresAt();
    return !!expiresAt && Date.now() >= expiresAt;
  }

  private isRefreshTokenExpiringSoon(): boolean {
    const expiresAt = this.getRefreshTokenExpiresAt();
    return !!expiresAt && Date.now() + this.refreshTokenRefreshBufferMs >= expiresAt;
  }

  private refreshSessionIfNeeded(forceRefresh = false): void {
    if (!this.hasRefreshToken()) {
      this.cancelSessionMaintenance();
      return;
    }

    if (this.isRefreshTokenExpired()) {
      this.cancelSessionMaintenance();
      return;
    }

    if (!forceRefresh && !this.shouldRefreshAccessToken() && !this.isRefreshTokenExpiringSoon()) {
      this.scheduleSessionMaintenance();
      return;
    }

    this.refreshSession().subscribe(
      () => this.scheduleSessionMaintenance(),
      (error) => {
        if (this.shouldEndSessionForRefreshError(error)) {
          this.cancelSessionMaintenance();
          return;
        }

        this.scheduleSessionMaintenance(this.failedRefreshRetryMs);
      }
    );
  }

  private scheduleSessionMaintenance(delayMs: number = this.getNextMaintenanceDelayMs()): void {
    if (!this.hasRefreshToken()) {
      this.cancelSessionMaintenance();
      return;
    }

    this.cancelSessionMaintenance();
    const dueTime = delayMs <= 0 ? 0 : Math.max(delayMs, this.minimumMaintenanceDelayMs);
    this.sessionMaintenance$ = timer(dueTime).subscribe(() => this.refreshSessionIfNeeded(true));
  }

  private cancelSessionMaintenance(): void {
    if (this.sessionMaintenance$) {
      this.sessionMaintenance$.unsubscribe();
      this.sessionMaintenance$ = null;
    }
  }

  private getNextMaintenanceDelayMs(): number {
    const now = Date.now();
    const delays = [this.fallbackSessionMaintenanceMs];
    const accessTokenExpiresAt = this.getAccessTokenExpiresAt();
    const refreshTokenExpiresAt = this.getRefreshTokenExpiresAt();

    if (accessTokenExpiresAt) {
      delays.push(accessTokenExpiresAt - now - this.accessTokenRefreshBufferMs);
    }

    if (refreshTokenExpiresAt) {
      delays.push(refreshTokenExpiresAt - now - this.refreshTokenRefreshBufferMs);
    }

    return Math.min(...delays);
  }

  private normalizeEpochMs(value: number | string): number {
    if (!value) {
      return null;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue < 10_000_000_000 ? numericValue * 1000 : numericValue;
    }

    const parsedValue = Date.parse(String(value));
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  private getDeviceId(): string {
    const storageKey = 'authDeviceId';
    const existingDeviceId = localStorage.getItem(storageKey);
    if (existingDeviceId) {
      return existingDeviceId;
    }

    const deviceId = this.createDeviceId();
    localStorage.setItem(storageKey, deviceId);
    return deviceId;
  }

  private createDeviceId(): string {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('');
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private getPlatform(userAgent: string): string {
    const ua = userAgent.toLowerCase();
    if (ua.includes('android')) {
      return 'android';
    }
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ios')) {
      return 'ios';
    }
    return 'web';
  }

  private getDeviceName(userAgent: string): string {
    if (typeof window !== 'undefined' && window.location?.hostname) {
      return `KDS ${window.location.hostname}`;
    }
    return userAgent.slice(0, 255);
  }

  /**
   * clear token
   */
  public clearToken() {
    this.stopSessionMaintenance();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('accessTokenExpiresAt');
    localStorage.removeItem('refreshTokenExpiresAt');
    localStorage.removeItem('authSessionId');
  }
}
